import { useMemo, useRef } from 'react';
import { figureSamples } from '../engine/display/figures';
import { nextOccluded } from '../engine/display/occlusion';

const NONE = {};

/**
 * Which figures stand behind terrain on the display, by token id (true only).
 *
 * `hider` is what can hide a figure under this camera frame (engine/display/occlusion `Hider`): the svg map's opaque
 * faces, or the GPU map's opaque solids - the display hands one down and this never asks which. Null off the
 * display, or when nothing could hide anyone, and then nothing is occluded.
 *
 * The answer carries HYSTERESIS across renders (engine/display/occlusion): each figure's last state is remembered,
 * so a figure at a wall's edge keeps its state while the camera drifts, and only the figures on the map now are
 * remembered. Each token's hidden fraction is cached on the token object until the hider changes (a camera frame,
 * new terrain), so a drag (a new token list, one token moved) recomputes one figure, not forty. Re-running for the
 * same inputs gives the same answer: the state is a fixed point.
 */
export function useDisplayOcclusion(tokens, hider) {
  const memory = useRef(new Map());
  const fractions = useRef({ for: null, byToken: new WeakMap() });
  return useMemo(() => {
    if (!hider) {
      // Off the display, or nothing that could hide anyone (a map with no smoke, for the half-seen pass): forget,
      // so the next hider judges everyone afresh.
      memory.current.clear();
      return NONE;
    }
    if (fractions.current.for !== hider) fractions.current = { for: hider, byToken: new WeakMap() };
    const { byToken } = fractions.current;
    const fractionOf = (t) => {
      if (!byToken.has(t)) byToken.set(t, hider.fraction(figureSamples(t)));
      return byToken.get(t);
    };
    const next = new Map();
    const out = {};
    for (const t of tokens) {
      const now = nextOccluded(memory.current.get(t.id) ?? false, fractionOf(t));
      next.set(t.id, now);
      if (now) out[t.id] = true;
    }
    memory.current = next;
    return out;
  }, [tokens, hider]);
}
